import { ethers } from 'ethers';
import transactionService from './transactionService';

// Admin addresses that can access the dashboard
const ADMIN_ADDRESSES = [
  // Add your admin addresses here, lowercase
  "0x267b6933afb44989fb8230e60d5d90bae6511f43", // Replace with your address
];

/**
 * Service for admin-only contract management functions
 */
class AdminService {
  /**
   * Create a new AdminService instance
   * @param {Object} contracts - Object containing all contract service instances
   */
  constructor(contracts) {
    this.contracts = contracts;
    
    // Initialize logging
    this.logPrefix = '[AdminService]';
    console.log(`${this.logPrefix} Initialized`);
  }

  /**
   * Helper method for logging errors with context
   * @private
   * @param {string} method - Method name where error occurred
   * @param {Error} error - The error object
   */
  _logError(method, error) {
    console.error(`${this.logPrefix} Error in ${method}:`, error);
    
    // Additional contextual information for debugging
    if (error.code) {
      console.error(`${this.logPrefix} Error code:`, error.code);
    }
    
    if (error.reason) {
      console.error(`${this.logPrefix} Error reason:`, error.reason);
    }
    
    if (error.message) {
      console.error(`${this.logPrefix} Error message:`, error.message);
    }
    
    if (error.data) {
      console.error(`${this.logPrefix} Error data:`, error.data);
    }
    
    if (error.transaction) {
      console.error(`${this.logPrefix} Transaction:`, {
        from: error.transaction.from,
        to: error.transaction.to,
        data: error.transaction.data?.substring(0, 66) + '...'
      });
    }
  }

  /**
   * Check if an address is an admin
   * @param {string} address - The address to check
   * @returns {Promise<boolean>} - True if the address is an admin
   */
  async isAdmin(address) {
    try {
      if (!address) return false;
      
      // First check our hardcoded list of admin addresses
      if (ADMIN_ADDRESSES.includes(address.toLowerCase())) {
        return true;
      }
      
      // Then check if the address has ADMIN_ROLE in EmissionController
      // Get the ADMIN_ROLE hash from the contract
      const ADMIN_ROLE = await this.contracts.emissionController.contract.DEFAULT_ADMIN_ROLE();
      
      // Check if the address has the admin role
      const hasAdminRole = await this.contracts.emissionController.contract.hasRole(
        ADMIN_ROLE,
        address
      );
      
      return hasAdminRole;
    } catch (error) {
      this._logError('isAdmin', error);
      return false;
    }
  }

  // ---- EmissionController Admin Functions ----

  /**
   * Start a new emission epoch
   * @returns {Promise<Object>} - Transaction result
   */
  async startEpoch() {
    try {
      const tx = this.contracts.emissionController.contract.startEpoch();
      return await transactionService.sendTransaction(tx, {
        title: 'Start Epoch',
        description: 'Starting a new emission epoch',
        type: 'admin_start_epoch'
      });
    } catch (error) {
      this._logError('startEpoch', error);
      throw error;
    }
  }

  /**
   * Process the current epoch
   * @returns {Promise<Object>} - Transaction result
   */
  async processEpoch() {
    try {
      const tx = this.contracts.emissionController.contract.processEpoch();
      return await transactionService.sendTransaction(tx, {
        title: 'Process Epoch',
        description: 'Processing the current emission epoch',
        type: 'admin_process_epoch'
      });
    } catch (error) {
      this._logError('processEpoch', error);
      throw error;
    }
  }

  /**
   * Force stop the current epoch without processing (for debugging)
   * @returns {Promise<Object>} Transaction receipt
   */
  async forceStopEpoch() {
    try {
      const contract = this.contracts.emissionController.contract;
      const tx = await contract.forceStopEpoch();
      const receipt = await tx.wait();
      
      console.log('Epoch force stopped for debugging:', receipt);
      return receipt;
    } catch (error) {
      this._logError('forceStopEpoch', error);
      throw error;
    }
  }

  /**
   * Set metrics score for a project
   * @param {number} projectId - Project ID
   * @param {number} score - Metrics score to set
   * @returns {Promise<Object>} - Transaction result
   */
  async setProjectMetricsScore(projectId, score) {
    try {
      const tx = this.contracts.emissionController.contract.setProjectMetricsScore(
        projectId,
        ethers.utils.parseUnits(score.toString(), 0)
      );
      return await transactionService.sendTransaction(tx, {
        title: 'Set Project Metrics Score',
        description: `Setting metrics score for project #${projectId}`,
        type: 'admin_set_project_metrics'
      });
    } catch (error) {
      this._logError('setProjectMetricsScore', error);
      throw error;
    }
  }

  /**
   * Set global metrics score
   * @param {number} score - Global metrics score to set
   * @returns {Promise<Object>} - Transaction result
   */
  async setGlobalMetricsScore(score) {
    try {
      const tx = this.contracts.emissionController.contract.setGlobalMetricsScore(
        ethers.utils.parseUnits(score.toString(), 0)
      );
      return await transactionService.sendTransaction(tx, {
        title: 'Set Global Metrics Score',
        description: 'Setting global metrics score',
        type: 'admin_set_global_metrics'
      });
    } catch (error) {
      this._logError('setGlobalMetricsScore', error);
      throw error;
    }
  }

  /**
   * Set epoch duration
   * @param {number} durationInDays - Epoch duration in days
   * @returns {Promise<Object>} - Transaction result
   */
  async setEpochDuration(durationInDays) {
    try {
      // Convert days to seconds
      const durationInSeconds = durationInDays * 24 * 60 * 60;
      
      const tx = this.contracts.emissionController.contract.setEpochDuration(
        durationInSeconds
      );
      return await transactionService.sendTransaction(tx, {
        title: 'Set Epoch Duration',
        description: `Setting epoch duration to ${durationInDays} days`,
        type: 'admin_set_epoch_duration'
      });
    } catch (error) {
      this._logError('setEpochDuration', error);
      throw error;
    }
  }

  /**
   * Set base emissions per epoch
   * @param {number} amount - Base emissions amount
   * @returns {Promise<Object>} - Transaction result
   */
  async setBaseEmissionsPerEpoch(amount) {
    try {
      const tx = this.contracts.emissionController.contract.setBaseEmissionsPerEpoch(
        ethers.utils.parseEther(amount.toString())
      );
      return await transactionService.sendTransaction(tx, {
        title: 'Set Base Emissions',
        description: `Setting base emissions per epoch to ${amount} CEL`,
        type: 'admin_set_base_emissions'
      });
    } catch (error) {
      this._logError('setBaseEmissionsPerEpoch', error);
      throw error;
    }
  }

  /**
   * Set maximum emissions per epoch
   * @param {number} amount - Maximum emissions amount
   * @returns {Promise<Object>} - Transaction result
   */
  async setMaxEmissionsPerEpoch(amount) {
    try {
      const tx = this.contracts.emissionController.contract.setMaxEmissionsPerEpoch(
        ethers.utils.parseEther(amount.toString())
      );
      return await transactionService.sendTransaction(tx, {
        title: 'Set Max Emissions',
        description: `Setting maximum emissions per epoch to ${amount} CEL`,
        type: 'admin_set_max_emissions'
      });
    } catch (error) {
      this._logError('setMaxEmissionsPerEpoch', error);
      throw error;
    }
  }

  /**
   * Set global weights for staking and metrics scores
   * @param {number} stakingWeight - Staking score weight (0-100)
   * @param {number} metricsWeight - Metrics score weight (0-100)
   * @returns {Promise<Object>} - Transaction result
   */
  async setGlobalWeights(stakingWeight, metricsWeight) {
    try {
      // Convert percentages to basis points (100% = 10000)
      const stakingBasisPoints = stakingWeight * 100;
      const metricsBasisPoints = metricsWeight * 100;
      
      const tx = this.contracts.emissionController.contract.setGlobalWeights(
        stakingBasisPoints,
        metricsBasisPoints
      );
      return await transactionService.sendTransaction(tx, {
        title: 'Set Global Weights',
        description: `Setting global weights: Staking ${stakingWeight}%, Metrics ${metricsWeight}%`,
        type: 'admin_set_global_weights'
      });
    } catch (error) {
      this._logError('setGlobalWeights', error);
      throw error;
    }
  }

  /**
   * Set emission shares between stakers and IU holders
   * @param {number} stakingShare - Staking share (0-100)
   * @param {number} iuHoldersShare - IU holders share (0-100)
   * @returns {Promise<Object>} - Transaction result
   */
  async setEmissionShares(stakingShare, iuHoldersShare) {
    try {
      // Convert percentages to basis points (100% = 10000)
      const stakingBasisPoints = stakingShare * 100;
      const iuHoldersBasisPoints = iuHoldersShare * 100;
      
      const tx = this.contracts.emissionController.contract.setEmissionShares(
        stakingBasisPoints,
        iuHoldersBasisPoints
      );
      return await transactionService.sendTransaction(tx, {
        title: 'Set Emission Shares',
        description: `Setting emission shares: Staking ${stakingShare}%, IU Holders ${iuHoldersShare}%`,
        type: 'admin_set_emission_shares'
      });
    } catch (error) {
      this._logError('setEmissionShares', error);
      throw error;
    }
  }

  /**
   * Emergency withdraw tokens from EmissionController
   * @param {string} token - Token address
   * @param {number} amount - Amount to withdraw
   * @returns {Promise<Object>} - Transaction result
   */
  async emergencyWithdrawFromEmissionController(token, amount) {
    try {
      const tx = this.contracts.emissionController.contract.emergencyWithdraw(
        token,
        ethers.utils.parseEther(amount.toString())
      );
      return await transactionService.sendTransaction(tx, {
        title: 'Emergency Withdraw',
        description: `Emergency withdrawing ${amount} tokens from EmissionController`,
        type: 'admin_emergency_withdraw'
      });
    } catch (error) {
      this._logError('emergencyWithdrawFromEmissionController', error);
      throw error;
    }
  }

  // ---- InnovationUnits Admin Functions ----

  /**
   * Update buy fee percentage
   * @param {number} percentage - Fee percentage (0-30)
   * @returns {Promise<Object>} - Transaction result
   */
  async updateBuyFeePercentage(percentage) {
    try {
      // Convert percentage to basis points (1% = 100 basis points)
      const basisPoints = percentage * 100;
      
      const tx = this.contracts.innovationUnits.contract.updateBuyFeePercentage(
        basisPoints
      );
      return await transactionService.sendTransaction(tx, {
        title: 'Update Buy Fee',
        description: `Updating buy fee percentage to ${percentage}%`,
        type: 'admin_update_buy_fee'
      });
    } catch (error) {
      this._logError('updateBuyFeePercentage', error);
      throw error;
    }
  }

  /**
   * Update sell fee percentage
   * @param {number} percentage - Fee percentage (0-30)
   * @returns {Promise<Object>} - Transaction result
   */
  async updateSellFeePercentage(percentage) {
    try {
      // Convert percentage to basis points (1% = 100 basis points)
      const basisPoints = percentage * 100;
      
      const tx = this.contracts.innovationUnits.contract.updateSellFeePercentage(
        basisPoints
      );
      return await transactionService.sendTransaction(tx, {
        title: 'Update Sell Fee',
        description: `Updating sell fee percentage to ${percentage}%`,
        type: 'admin_update_sell_fee'
      });
    } catch (error) {
      this._logError('updateSellFeePercentage', error);
      throw error;
    }
  }

  // ---- ProjectStaking Admin Functions ----

  /**
   * Set minimum lock duration for staking
   * @param {number} durationInDays - Minimum lock duration in days
   * @returns {Promise<Object>} - Transaction result
   */
  async setMinLockDuration(durationInDays) {
    try {
      // Convert days to seconds
      const durationInSeconds = durationInDays * 24 * 60 * 60;
      
      const tx = this.contracts.projectStaking.contract.setMinLockDuration(
        durationInSeconds
      );
      return await transactionService.sendTransaction(tx, {
        title: 'Set Min Lock Duration',
        description: `Setting minimum lock duration to ${durationInDays} days`,
        type: 'admin_set_min_lock_duration'
      });
    } catch (error) {
      this._logError('setMinLockDuration', error);
      throw error;
    }
  }

  /**
   * Set maximum lock duration for staking
   * @param {number} durationInDays - Maximum lock duration in days
   * @returns {Promise<Object>} - Transaction result
   */
  async setMaxLockDuration(durationInDays) {
    try {
      // Convert days to seconds
      const durationInSeconds = durationInDays * 24 * 60 * 60;
      
      const tx = this.contracts.projectStaking.contract.setMaxLockDuration(
        durationInSeconds
      );
      return await transactionService.sendTransaction(tx, {
        title: 'Set Max Lock Duration',
        description: `Setting maximum lock duration to ${durationInDays} days`,
        type: 'admin_set_max_lock_duration'
      });
    } catch (error) {
      this._logError('setMaxLockDuration', error);
      throw error;
    }
  }

  /**
   * Set multiplier factor for staking score
   * @param {number} factor - Multiplier factor (1-19)
   * @returns {Promise<Object>} - Transaction result
   */
  async setMultiplierFactor(factor) {
    try {
      // Convert to contract precision
      const SCORE_PRECISION = ethers.BigNumber.from('1000000000000'); // 1e12
      const factorWithPrecision = SCORE_PRECISION.mul(factor);
      
      const tx = this.contracts.projectStaking.contract.setMultiplierFactor(
        factorWithPrecision
      );
      return await transactionService.sendTransaction(tx, {
        title: 'Set Multiplier Factor',
        description: `Setting staking multiplier factor to ${factor}`,
        type: 'admin_set_multiplier_factor'
      });
    } catch (error) {
      this._logError('setMultiplierFactor', error);
      throw error;
    }
  }

  // ---- ProtocolTreasury Admin Functions ----

  /**
   * Set project creation fee
   * @param {number} amount - Fee amount in ETH
   * @returns {Promise<Object>} - Transaction result
   */
  async setProjectCreationFee(amount) {
    try {
      const tx = this.contracts.protocolTreasury.contract.setProjectCreationFee(
        ethers.utils.parseEther(amount.toString())
      );
      return await transactionService.sendTransaction(tx, {
        title: 'Set Project Fee',
        description: `Setting project creation fee to ${amount} ETH`,
        type: 'admin_set_project_fee'
      });
    } catch (error) {
      this._logError('setProjectCreationFee', error);
      throw error;
    }
  }

  /**
   * Set token sale fee percentage
   * @param {number} percentage - Fee percentage (0-10)
   * @returns {Promise<Object>} - Transaction result
   */
  async setTokenSaleFee(percentage) {
    try {
      // Convert percentage to basis points (1% = 100 basis points)
      const basisPoints = percentage * 100;
      
      const tx = this.contracts.protocolTreasury.contract.setTokenSaleFeePercent(
        basisPoints
      );
      return await transactionService.sendTransaction(tx, {
        title: 'Set Token Sale Fee',
        description: `Setting token sale fee to ${percentage}%`,
        type: 'admin_set_token_sale_fee'
      });
    } catch (error) {
      this._logError('setTokenSaleFee', error);
      throw error;
    }
  }

  /**
   * Send fees from the protocol treasury
   * @param {string} recipient - Recipient address
   * @param {number} amount - Amount to send
   * @returns {Promise<Object>} - Transaction result
   */
  async sendFeesFromTreasury(recipient, amount) {
    try {
      console.log('sendFeesFromTreasury called with recipient:', recipient, 'amount:', amount);
      
      if (this.contracts.protocolTreasury.sendFees && typeof this.contracts.protocolTreasury.sendFees === 'function') {
        console.log('Using protocolTreasury.sendFees() method');
        return await this.contracts.protocolTreasury.sendFees(recipient, amount);
      } else {
        console.log('Using protocolTreasury.contract.sendFees() method');
        const tx = this.contracts.protocolTreasury.contract.sendFees(
          recipient,
          ethers.utils.parseEther(amount.toString())
        );
        
        return await transactionService.sendTransaction(tx, {
          title: 'Send Treasury Fees',
          description: `Sending ${amount} CEL from treasury to ${recipient}`,
          type: 'admin_send_fees'
        });
      }
    } catch (error) {
      this._logError('sendFeesFromTreasury', error);
      throw error;
    }
  }
}

export default AdminService; 